import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Share2, Gift, Mail } from "lucide-react";

function buildShareMessage(shareCode) {
  const shareUrl = `${window.location.origin}/book?ref=${shareCode}`;
  const message = `I'm going to Picnic in the Park · The Genesis on Grinda (19–20 Aug 2026). Join me with my code ${shareCode} for 10% off — ${shareUrl}`;
  return { shareUrl, message };
}

function ShareLinks({ message, shareUrl }) {
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const emailHref = `mailto:?subject=${encodeURIComponent(
    "Come to Picnic in the Park with me"
  )}&body=${encodeURIComponent(message)}`;
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      <a
        href={whatsapp}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-2 text-xs font-bold tracking-wide uppercase rounded-round bg-sand text-navy px-4 py-2 hover:bg-sand/80"
        data-testid="share-whatsapp"
      >
        WhatsApp
      </a>
      <a
        href={emailHref}
        className="inline-flex items-center gap-2 text-xs font-bold tracking-wide uppercase rounded-round bg-sand text-navy px-4 py-2 hover:bg-sand/80"
        data-testid="share-email"
      >
        <Mail size={12} /> Email
      </a>
      <Link
        to="/invite"
        className="inline-flex items-center gap-2 text-xs font-bold tracking-wide uppercase rounded-round bg-navy/5 text-navy px-4 py-2 hover:bg-navy/10"
      >
        <Gift size={12} /> See rewards
      </Link>
    </div>
  );
}

export default function ShareCard({ shareCode }) {
  const { shareUrl, message } = buildShareMessage(shareCode);
  const [copied, setCopied] = useState(false);

  async function copyUrl() {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard unavailable — user can copy code manually
    }
  }

  async function nativeShare() {
    if (!navigator.share) return copyUrl();
    try {
      await navigator.share({
        title: "Picnic in the Park",
        text: message,
        url: shareUrl,
      });
    } catch {
      // user cancelled or share unavailable — silent
    }
  }

  return (
    <div
      className="mt-8 card p-6 md:p-8 text-left relative overflow-hidden"
      data-testid="share-card"
    >
      <div
        className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-coral/10"
        aria-hidden
      />
      <p className="eyebrow">Bring a friend · get 10% off each</p>
      <h2 className="heading-md mt-1">
        Your code for <span className="italic-accent">friends</span>
      </h2>
      <p className="text-grey text-sm mt-2">
        Share this code and every friend saves 10% on their booking. A little
        thank-you from us for spreading the joy.
      </p>

      <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <div
          className="flex-1 rounded-xl border-2 border-dashed border-coral bg-coral/5 p-4 text-center"
          data-testid="share-code"
        >
          <p className="text-[10px] font-bold tracking-widest uppercase text-coral">
            Your invite code
          </p>
          <p className="font-serif text-3xl tracking-widest text-navy mt-1">
            {shareCode}
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:w-44">
          <button
            onClick={nativeShare}
            className="btn-primary justify-center"
            data-testid="share-native-btn"
          >
            <Share2 size={16} /> Share
          </button>
          <button
            onClick={copyUrl}
            className="inline-flex items-center justify-center gap-2 rounded-round border border-navy/10 text-navy font-bold px-4 py-2 text-sm hover:bg-navy/5"
            data-testid="share-copy-btn"
          >
            <Copy size={14} /> {copied ? "Copied!" : "Copy message"}
          </button>
        </div>
      </div>

      <ShareLinks message={message} shareUrl={shareUrl} />
    </div>
  );
}
